/**
 * El lienzo de fabric y su editor, en casa.
 *
 * ── Por qué existe ─────────────────────────────────────────────────────────
 *
 * Esto era `fabricjs-react`, un envoltorio de ~150 líneas sin publicar desde
 * hace unos dos años. Su `peerDependency` es `fabric: ^5 || ^6`, así que era
 * LO ÚNICO que impedía subir a fabric 7 — la 7.4.0 no tiene ningún aviso de
 * seguridad y la 6.9.1 sí (XSS almacenado al serializar SVG, GHSA-hfvx-25r5-qc3w
 * y GHSA-w22m-hvvm-xmwx). npm decía "no fix available" precisamente por ese
 * peer, no porque no hubiera versión arreglada.
 *
 * De todo lo que exportaba, el croquis de ROIS usa cuatro cosas: `canvas`,
 * `deleteAll`, `deleteSelected` y `onReady`. Lo demás (addCircle, addLine,
 * zoomIn, setFillColor…) nunca se llamó: las figuras se crean a mano en
 * `ActionSketch` y `AccidentSketch` con sus propios estilos.
 *
 * ── Qué se ha conservado tal cual ──────────────────────────────────────────
 *
 * El dimensionado, que es lo delicado: el `<canvas>` NO tiene tamaño propio,
 * lo toma del div que lo envuelve en cada `resize`. Si esto cambiara, el
 * croquis se dibujaría a una escala y se guardaría a otra. Se replica el
 * comportamiento original al detalle, incluido el escuchador de `resize` de
 * `window` y el `dispose()` al desmontar.
 *
 * Única diferencia deliberada: el original hacía `console.log('Fabric canvas
 * ready')` cada vez que se montaba.
 */

import { useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { Canvas, FabricObject, type FabricObject as FabricObjectType } from 'fabric';

/**
 * 🔴 Fabric 7 cambió el origen por defecto de los objetos a su CENTRO.
 *
 * Medido: `new Rect({ left: 0, top: 0, width: 100, height: 50 })` da un
 * bounding box en (0, 0) con fabric 6 y en (−50,5, −25,5) con fabric 7. Sin
 * esto, TODAS las figuras del croquis se desplazarían media medida — las que
 * se dibujan ahora y, peor, las de los croquis ya guardados al recargarlos
 * con `loadSVGFromString`.
 *
 * Se restaura el comportamiento de fabric 6 en un punto único, antes de que
 * se construya ningún objeto. Va aquí, en el módulo del lienzo, porque es el
 * que importan los dos croquis y no hay forma de dibujar sin pasar por él.
 */
FabricObject.ownDefaults.originX = 'left';
FabricObject.ownDefaults.originY = 'top';

export interface FabricJSEditor {
  canvas: Canvas;
  /** Vacía el lienzo. */
  deleteAll: () => void;
  /** Borra lo que esté seleccionado. */
  deleteSelected: () => void;
}

export interface FabricJSEditorHook {
  editor?: FabricJSEditor;
  selectedObjects?: FabricObjectType[];
  onReady: (canvas: Canvas) => void;
}

/**
 * Expone el lienzo ya montado y las dos operaciones de borrado.
 *
 * El editor se memoriza contra el lienzo: mientras sea el mismo, los
 * consumidores reciben la misma referencia y no se re-renderizan de más.
 */
export const useFabricJSEditor = (): FabricJSEditorHook => {
  const [canvas, setCanvas] = useState<Canvas | undefined>(undefined);
  const [selectedObjects, setSelectedObjects] = useState<FabricObjectType[]>([]);

  useEffect(() => {
    if (!canvas) return undefined;

    const onSelection = (event: { selected?: FabricObjectType[] }): void => {
      setSelectedObjects(event.selected ?? []);
    };
    const onCleared = (): void => setSelectedObjects([]);

    canvas.on('selection:created', onSelection);
    canvas.on('selection:updated', onSelection);
    canvas.on('selection:cleared', onCleared);

    return () => {
      canvas.off('selection:created', onSelection);
      canvas.off('selection:updated', onSelection);
      canvas.off('selection:cleared', onCleared);
    };
  }, [canvas]);

  const editor = useMemo<FabricJSEditor | undefined>(() => {
    if (!canvas) return undefined;

    return {
      canvas,
      deleteAll: () => {
        canvas.getObjects().forEach((object) => canvas.remove(object));
        canvas.discardActiveObject();
        canvas.renderAll();
      },
      deleteSelected: () => {
        canvas.getActiveObjects().forEach((object) => canvas.remove(object));
        canvas.discardActiveObject();
        canvas.renderAll();
      },
    };
  }, [canvas]);

  return { editor, selectedObjects, onReady: setCanvas };
};

export interface FabricJSCanvasProps {
  className?: string;
  onReady?: (canvas: Canvas) => void;
}

/**
 * El `<canvas>` con su envoltorio, que es quien manda en el tamaño.
 */
export const FabricJSCanvas = ({ className, onReady }: FabricJSCanvasProps): ReactElement => {
  const canvasElement = useRef<HTMLCanvasElement>(null);
  const wrapper = useRef<HTMLDivElement>(null);

  // El array de dependencias va VACÍO a propósito, igual que en el original:
  // el lienzo se crea una sola vez por montaje. Meter `onReady` aquí lo
  // recrearía en cada render del padre (la prop suele ser una función
  // literal), y con él se perdería todo lo dibujado.
  useEffect(() => {
    const canvas = new Canvas(canvasElement.current ?? undefined);

    // 🔴 Fabric 7 retiró `setWidth()` y `setHeight()`, que es justo lo que
    // llamaba `fabricjs-react`: aunque se le hubiera forzado el peer, se
    // habría roto aquí en tiempo de ejecución, sin que ningún tipo avisara.
    // El sustituto es `setDimensions`, que además aplica ambas en una sola
    // pasada de recálculo.
    const resize = (): void => {
      canvas.setDimensions({
        width: wrapper.current?.clientWidth || 0,
        height: wrapper.current?.clientHeight || 0,
      });
      canvas.renderAll();
    };

    resize();
    window.addEventListener('resize', resize, false);
    onReady?.(canvas);

    return () => {
      canvas.dispose();
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <div ref={wrapper} className={className}>
      <canvas ref={canvasElement} />
    </div>
  );
};
